import React, { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  startProgress?: number;
  endProgress?: number;
  yOffset?: number;
  useBlur?: boolean;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  delay = 0,
  startProgress = 0.55,
  endProgress = 0.80,
  yOffset = 12,
  useBlur = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const prefersReducedMotion = useMemo(() => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Adjust progress range based on delay
  const adjustedStart = Math.min(startProgress + delay, 0.95);
  const adjustedEnd = Math.min(endProgress + delay, 1.0);

  const opacity = useTransform(
    scrollYProgress,
    [adjustedStart, adjustedEnd],
    [0, 1]
  );

  const y = useTransform(
    scrollYProgress,
    [adjustedStart, adjustedEnd],
    prefersReducedMotion ? [0, 0] : [yOffset, 0]
  );

  const blur = useTransform(
    scrollYProgress,
    [adjustedStart, adjustedEnd],
    prefersReducedMotion || !useBlur ? [0, 0] : [4, 0]
  );

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        opacity,
        y,
        filter: useTransform(blur, (val) => `blur(${val}px)`),
      }}
    >
      {children}
    </motion.div>
  );
};
